import { Navigation } from '~/shared/routing/nav/Navigation';
import { AlertOctagon, CheckCircle2, Circle, CircleMinus } from 'lucide-react';
import { getVisitRoutePath } from '../../pages/visits/utils';
import { InterventionTaskStatusEnum, WorkflowStageTaskStatusEnum, type FullVisitBucketCategoryViewModel } from '@generated';
import { useGetFullVisitOptions } from './queries';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

export function VisitNavigation({ stayId, visitId, selectedBucketId }: { stayId: string; visitId: string; selectedBucketId: string }) {
	const { data: visit } = useSuspenseQuery(useGetFullVisitOptions(visitId));

	const selectedVisitBucket = visit.visitBuckets.find(x => x.id === selectedBucketId)!;

	if (!selectedVisitBucket || !selectedVisitBucket.orderedItems || selectedVisitBucket.orderedItems.length === 0) {
		return null;
	}

	return (
		<nav className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
			{selectedVisitBucket.orderedItems.map(item => {
				if (item.category) {
					const category = item.category;
					return (
						<VisitNavigationLink
							key={item.id}
							label={category.label}
							path={getVisitRoutePath(stayId, visitId, selectedVisitBucket.id, item.id)}
							icon={selected => <VisitCategoryNavigationStatusIcon selected={selected} category={category} />}
						/>
					);
				} else if (item.interventionTask) {
					const interventionTask = item.interventionTask;
					return (
						<VisitNavigationLink
							key={item.id}
							label={
								interventionTask.interventionDefinition && interventionTask.interventionDefinition.menuLabel
									? interventionTask.interventionDefinition.menuLabel
									: interventionTask.title
							}
							path={getVisitRoutePath(stayId, visitId, selectedVisitBucket.id, item.id)}
							icon={selected => (
								<InterventionTaskNavigationStatusIcon
									selected={selected}
									interventionTaskStatusId={interventionTask.interventionTaskStatusId}
								/>
							)}
						/>
					);
				} else if (item.oasisTask) {
					const oasisTask = item.oasisTask;
					return (
						<VisitNavigationLink
							key={item.id}
							label={oasisTask.oasisDefinition.menuLabel ?? oasisTask.oasisDefinition.title}
							path={getVisitRoutePath(stayId, visitId, selectedVisitBucket.id, item.id)}
							icon={selected => (
								<InterventionTaskNavigationStatusIcon selected={selected} interventionTaskStatusId={oasisTask.oasisTaskStatusId} />
							)}
						/>
					);
				} else if (item.workflowStageTask) {
					const workflowStageTask = item.workflowStageTask;
					return (
						<Navigation.NavigationLink
							key={item.id}
							label={workflowStageTask.menuLabel}
							path={getVisitRoutePath(stayId, visitId, selectedVisitBucket.id, item.id)}
							className="flex-row"
							matchSearchParams
						>
							{selected => {
								return (
									<WorkflowStageTaskNavigationStatusIcon workflowStageTaskStatusId={workflowStageTask.workflowStageTaskStatusId} />
								);
							}}
						</Navigation.NavigationLink>
					);
				}
				return null;
			})}
		</nav>
	);
}

function VisitNavigationLink({ label, path, icon }: { label: string; path: string; icon: (selected: boolean) => ReactNode }) {
	return (
		<Navigation.NavigationLink label={label} path={path} className="flex-row" matchSearchParams>
			{selected => icon(selected)}
		</Navigation.NavigationLink>
	);
}

function WorkflowStageTaskNavigationStatusIcon({ workflowStageTaskStatusId }: { workflowStageTaskStatusId: number }) {
	switch (workflowStageTaskStatusId) {
		case WorkflowStageTaskStatusEnum.Completed:
			return <CheckCircle2 className="size-4 text-[#296747]" />;
		case WorkflowStageTaskStatusEnum.CancelledByWorkflow:
			return <AlertOctagon className="size-4 text-red-500" />;
		case WorkflowStageTaskStatusEnum.NeedsCompleted:
			return <Circle className="size-4 text-gray-300" />;
		default:
			throw new Error('Unknown WorkflowStageTaskStatusEnum value');
	}
}

function InterventionTaskNavigationStatusIcon({ interventionTaskStatusId, selected }: { interventionTaskStatusId: number; selected: boolean }) {
	switch (interventionTaskStatusId) {
		case InterventionTaskStatusEnum.Performed:
			return <CheckCircle2 className="size-4 text-[#296747]" />;
		case InterventionTaskStatusEnum.CancelledByWorkflow:
			return <AlertOctagon className="size-4 text-red-700" />;
		case InterventionTaskStatusEnum.DidNotPerform:
			return <CircleMinus className="size-4 text-gray-500" />;
		case InterventionTaskStatusEnum.NeedsPerformed:
			return <Circle className="size-4 text-gray-300" />;
		default:
			throw new Error('Unknown WorkflowStageTaskStatusEnum value');
	}
}

function VisitCategoryNavigationStatusIcon({ category, selected }: { category: FullVisitBucketCategoryViewModel; selected: boolean }) {
	const completedTasksCount =
		category.interventionTasks.length > 0
			? category.interventionTasks.filter(x => x.interventionTaskStatusId !== WorkflowStageTaskStatusEnum.NeedsCompleted).length
			: category.oasisTasks.length > 0
			? category.oasisTasks.filter(x => x.oasisTaskStatusId !== WorkflowStageTaskStatusEnum.NeedsCompleted).length
			: 0;

	const totalTasksCount = category.interventionTasks.length > 0 ? category.interventionTasks.length : category.oasisTasks.length;

	if (completedTasksCount === totalTasksCount) {
		return <CheckCircle2 className="size-4 text-[#296747]" />;
	} else if (selected) {
		return <Circle className="size-4 text-[#296747]" />;
	}

	return <Circle className="size-4 text-gray-300" />;
}
